import { dateFormatMonthYear, flattenApiPages, PropertyReviews, usePropertyReviewsInfiniteQuery } from '@alluradirect/react';
import { Box, Button, Rating, Typography } from '@mui/material';
import { AppDivider } from 'components/core/AppDivider/AppDivider';
import ConfirmationDialog from 'components/core/ConfirmationDialog/ConfirmationDialog';
import Grid from 'components/core/Grid';
import { LabelAndValue } from 'components/core/LabelAndValue/LabelAndValue';
import { LoadingIndicator } from 'components/core/LoadingIndicator/LoadingIndicator';
import { UserProfileIcon } from 'components/core/UserProfileIcon/UserProfileIcon';
import { useState } from 'react';
import styled from 'styled-components';

interface Props {
  isOpen: boolean;
  close: () => void;
  propertyId: number
}

export const TextLineClamp = styled(Typography)`
   text-overflow: ellipsis;
  display: -webkit-box;
  line-clamp: 1;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;  
  overflow: hidden;
`;

export const HostResponseTypography = styled(Typography)`
margin-left: ${props => props.theme.spacing(2)};
margin-top: ${props => props.theme.spacing(2)};
padding: ${props => props.theme.spacing(1)};
border: 1px solid ${props => props.theme.palette.grey[300]};
border-radius: 5px;
`;




export const CheckoutSummaryPropertyReviews = ({ isOpen, close, propertyId }: Props) => {
  const [initialRender, setInitilalRender] = useState(true);
  const [numReviews, setNumReviews] = useState(0);
  const [averages, setAverages] = useState<PropertyReviews['AVERAGE_SCORES']>([]);
  const [averageScore, setAverageScore] = useState(0);

  const { data, hasNextPage, fetchNextPage } = usePropertyReviewsInfiniteQuery({ propertyId });

  const info: PropertyReviews = data?.pages[0]?.DATA;
  const reviews: PropertyReviews['REVIEWS'] = flattenApiPages(data, 'REVIEWS');

  if (initialRender && data) {
    setNumReviews(info?.NUM_REVIEWS);
    setAverages(info?.AVERAGE_SCORES);
    setAverageScore(info?.AVERAGE_SCORE);
    setInitilalRender(false)
  }


  const fetchMoreReviews = async () => {
    const { data } = await fetchNextPage();
    if (data) {
      const info: PropertyReviews = data.pages[0]?.DATA;
      const averages = info.AVERAGE_SCORES
      setAverages(prev => [...prev, ...averages])
    }
  }

  return (
    <ConfirmationDialog
      isDialogOpen={isOpen}
      handleCloseDialog={close}
      hasBottomBar={false}
      dialogTitle={data ? (`${numReviews} Guest Reviews`) : `Loading Reviews`
      }
      useIsXs={false}
      maxWidth="sm"
    >
      {!data ? (
        <LoadingIndicator />
      ) : (
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <LabelAndValue label="Overall Rating" xs={10} value={averageScore} fontSize={20} />
            <AppDivider />
          </Grid>

          <Grid item xs={12}>
            {averages?.map((review, index) => (
              <LabelAndValue label={review.TITLE} xs={10} value={review.AVERAGE_SCORE} key={index} fontSize={16} />
            ))}
            <AppDivider />
          </Grid>

          <Grid item xs={12}>
            {reviews?.map((review, index) => {
              const guest = review?.GUEST_INFO
              const booking = review?.BOOKING_INFO
              const arrival = review.BOOKING_INFO?.ARRIVAL ? dateFormatMonthYear(booking?.ARRIVAL) : '--';
              return (
                <>
                  <Box display={"flex"} key={index}>
                    <UserProfileIcon image={guest?.THUMBNAIL} size={44} />
                    <Box>
                      <TextLineClamp variant="body1" fontWeight={500}>{guest?.FIRST_NAME}</TextLineClamp>
                      <TextLineClamp variant="body2">{guest?.CITY}, {guest?.COUNTRY}</TextLineClamp>
                    </Box>
                  </Box>
                  <Box mb={1}>
                    <Rating value={review?.RATINGS_AVERAGE} precision={0.5} size='small' />
                    <Typography variant="body2">Stayed {arrival}</Typography>
                  </Box>
                  <Box mb={2}>
                    <Typography variant="body2">
                      <div dangerouslySetInnerHTML={{ __html: review?.REVIEW }}></div>
                    </Typography>
                    {review?.RESPONSES?.map((response, index) => (
                      <HostResponseTypography variant="body2" key={index}><strong>Host response...</strong><br />
                        <div dangerouslySetInnerHTML={{ __html: response?.RESPONSE }}></div>
                      </HostResponseTypography>
                    ))}
                  </Box>
                  <AppDivider marginVertical='24px' />
                </>
              )
            })}

            {!!hasNextPage && <Box mb={5}><Button variant="outlined" color="secondary" fullWidth onClick={fetchMoreReviews}>Load More Reviews</Button></Box>}
          </Grid>
        </Grid>
      )}

    </ConfirmationDialog >
  );
};
